import type { AuditNames, GovernanceAuditEntry } from "@/lib/api/organizations";
import { formatBytes } from "@/lib/format";

/// A run of row text. A `name` part is a model, space or org name, inked like
/// the actor so a space called "another" cannot read as prose.
export type AuditPart = string | { name: string };

/// What one row says: `<actor> {verb} [<subject>] [{tail}]`. Split rather than
/// pre-rendered so the actor and subject can render as people with their
/// identity in a tooltip.
export interface AuditPhrase {
  verb: AuditPart[];
  subject: boolean;
  tail?: string;
  /// The verb already names the space, so the row must not also append it.
  namesSpace?: boolean;
  /// Which mark the row leads with, and whether it takes warning ink.
  kind?: AuditKind;
}

export type AuditKind =
  | "created"
  | "renamed"
  | "sealed"
  | "unsealed"
  | "granted"
  | "revoked"
  | "invited"
  | "joined"
  | "left"
  | "removed"
  | "role"
  | "doomed"
  | "restored"
  | "moved"
  | "deleted"
  | "billing"
  | "limit"
  | "reset";

const text = (
  detail: Record<string, unknown> | null,
  key: string,
): string | null => {
  const value = detail?.[key];
  return typeof value === "string" && value.length > 0 ? value : null;
};

const flag = (detail: Record<string, unknown> | null, key: string): boolean =>
  detail?.[key] === true;

const count = (
  detail: Record<string, unknown> | null,
  key: string,
): number | null => {
  const value = detail?.[key];
  if (typeof value === "number") return value;
  return Array.isArray(value) ? value.length : null;
};

/// Interpolated `AuditPart`s stay parts; the literal text between them stays text.
const words = (
  strings: TemplateStringsArray,
  ...parts: AuditPart[]
): AuditPart[] =>
  strings
    .flatMap((text, i) => (i < parts.length ? [text, parts[i]] : [text]))
    .filter((part) => part !== "");

const named = (name: string | null, fallback: string): AuditPart =>
  name ? { name } : fallback;

export const verbText = (verb: AuditPart[]): string =>
  verb.map((part) => (typeof part === "string" ? part : part.name)).join("");

const plural = (n: number, noun: string): string =>
  `${n} ${noun}${n === 1 ? "" : "s"}`;

/// "2 sub-spaces and 3 models", dropping whichever is zero or unrecorded.
const alsoWent = (
  detail: Record<string, unknown> | null,
  spacesKey: string,
  modelsKey: string,
  spaceNoun: string,
): string | null => {
  const parts: string[] = [];
  const spaces = count(detail, spacesKey);
  const models = count(detail, modelsKey);
  if (spaces) parts.push(plural(spaces, spaceNoun));
  if (models) parts.push(plural(models, "model"));
  return parts.length > 0 ? parts.join(" and ") : null;
};

/// The bare client id inside a recorded subject, which is an FGA subject
/// (`user:cli-…`) on the grant doors and bare everywhere else.
export const bareSubjectId = (subject: string): string =>
  subject.replace(/^user:/, "");

/// An act on oneself. D3 lets an org admin grant themselves into any space,
/// and making that read differently from an ordinary grant is the whole point
/// of keeping this log.
export const isSelfAct = (entry: GovernanceAuditEntry): boolean =>
  !!entry.subject && bareSubjectId(entry.subject) === entry.actor;

/// D39's classifications as English. An unrecognised value still shows — a
/// governance act this build cannot spell is worse hidden than raw.
const spaceNoun = (classification: string | null): string => {
  switch (classification) {
    case null:
      return "a space";
    case "open":
      return "an open space";
    case "restricted":
      return "a restricted space";
    case "sealed":
      return "a sealed space";
    default:
      return `a ${classification} space`;
  }
};

/// Names come from `names` — resolved when the log is read — never from the
/// row, except a rename's new name, which is what that row is about.
export function phraseFor(
  entry: GovernanceAuditEntry,
  names: Partial<AuditNames> = {},
): AuditPhrase {
  const detail = entry.detail;
  const self = isSelfAct(entry);
  const spaceOf = (id: string | null): string | null =>
    (id && names.spaces?.[id]) || null;
  const modelId = text(detail, "model_id");
  const model = named(
    (modelId && names.models?.[modelId]) || null,
    "an unknown model",
  );
  const space = spaceOf(entry.space_id);

  switch (entry.action) {
    case "space.grant": {
      const role = text(detail, "role") ?? "access";
      return self
        ? {
            verb: [`granted themselves ${role}`],
            subject: false,
            kind: "granted",
          }
        : { verb: [`granted ${role} to`], subject: true, kind: "granted" };
    }
    case "space.revoke": {
      const role = text(detail, "role") ?? "access";
      return self
        ? {
            verb: [`gave up their own ${role}`],
            subject: false,
            kind: "revoked",
          }
        : { verb: [`revoked ${role} from`], subject: true, kind: "revoked" };
    }
    // Historical rows: a starter space records `space.created` now, and the
    // fact was always identical, so hiding these would make the log lie by
    // omission.
    case "space.starter_minted":
    case "space.created": {
      const classification = text(detail, "classification");
      return {
        verb: space
          ? classification
            ? words`created ${{ name: space }} as ${spaceNoun(classification)}`
            : words`created ${{ name: space }}`
          : words`created ${spaceNoun(classification)}`,
        subject: false,
        namesSpace: space !== null,
        kind: "created",
      };
    }
    case "space.updated": {
      const name = text(detail, "name");
      const recoloured = text(detail, "color") !== null;
      if (name && recoloured) {
        return {
          verb: words`renamed it to ${{ name }} and recoloured it`,
          subject: false,
          namesSpace: true,
          kind: "renamed",
        };
      }
      if (name)
        return {
          verb: words`renamed it to ${{ name }}`,
          subject: false,
          namesSpace: true,
          kind: "renamed",
        };
      return { verb: ["recoloured it"], subject: false, kind: "renamed" };
    }
    case "space.sealed":
      return {
        verb: [
          flag(detail, "self_granted_manager")
            ? "sealed it, taking manager"
            : "sealed it",
        ],
        subject: false,
        kind: "sealed",
      };
    case "space.unsealed":
      return { verb: ["un-sealed it"], subject: false, kind: "unsealed" };
    case "space.deleted":
      return {
        verb: space ? words`deleted ${{ name: space }}` : ["deleted it"],
        subject: false,
        namesSpace: space !== null,
        kind: "deleted",
      };
    case "space.deletion_scheduled": {
      const also = alsoWent(detail, "sub_spaces", "models", "sub-space");
      return {
        verb: words`moved ${named(space, "the space")} to the Trash`,
        subject: false,
        namesSpace: space !== null,
        tail: also ? `with ${also}` : undefined,
        kind: "doomed",
      };
    }
    case "space.deletion_cancelled": {
      const withId = text(detail, "with");
      const root = named(spaceOf(withId), "the space above it");
      const subject = named(space, "the space");
      return {
        // A descendant row: it came back because the space above it did.
        verb: withId
          ? words`restored ${subject} along with ${root}`
          : words`restored ${subject} from the Trash`,
        subject: false,
        namesSpace: space !== null,
        kind: "restored",
      };
    }
    case "model.created":
      return {
        verb: words`created ${model}`,
        subject: false,
        kind: "created",
      };
    case "model.trashed": {
      const tables = count(detail, "tables_scheduled");
      return {
        verb: words`moved ${model} to the Trash`,
        subject: false,
        tail: tables ? `with ${plural(tables, "table")}` : undefined,
        kind: "doomed",
      };
    }
    case "model.restored":
      return {
        verb: words`restored ${model} from the Trash`,
        subject: false,
        kind: "restored",
      };
    // One row, listed on both spaces' logs, so it names both ends itself.
    case "model.moved": {
      const from = named(
        spaceOf(text(detail, "from_space_id")),
        "an unknown space",
      );
      const to = named(space, "an unknown space");
      return {
        verb: words`moved ${model} from ${from} to ${to}`,
        subject: false,
        namesSpace: true,
        kind: "moved",
      };
    }
    case "org.created":
      return {
        verb: ["created the organization"],
        subject: false,
        kind: "created",
      };
    case "org.deleted":
      return {
        verb: ["deleted the organization"],
        subject: false,
        kind: "deleted",
      };
    case "org.deletion_scheduled": {
      const also = alsoWent(
        detail,
        "spaces_scheduled",
        "models_trashed",
        "space",
      );
      return {
        verb: ["moved the organization to the Trash"],
        subject: false,
        tail: also ? `with ${also}` : undefined,
        kind: "doomed",
      };
    }
    case "org.deletion_cancelled":
      return {
        verb: ["restored the organization from the Trash"],
        subject: false,
        kind: "restored",
      };
    case "org.renamed": {
      const name = text(detail, "name");
      return {
        verb: name
          ? words`renamed the organization to ${{ name }}`
          : ["renamed the organization"],
        subject: false,
        kind: "renamed",
      };
    }
    case "org.image_changed":
      return {
        verb: [
          flag(detail, "removed") ? "removed the logo" : "changed the logo",
        ],
        subject: false,
        kind: "renamed",
      };
    /// Pre-D43 rows: membership was instant, and `pending` marked an invite
    /// to an address with no account. New acts write the verbs below.
    case "org.member_added":
      return {
        verb: [flag(detail, "pending") ? "invited" : "added"],
        subject: true,
        kind: "invited",
      };
    case "org.member_invited":
      return { verb: ["invited"], subject: true, kind: "invited" };
    case "org.invite_revoked":
      return {
        verb: ["cancelled the invitation for"],
        subject: true,
        kind: "removed",
      };
    case "org.invite_declined":
      return {
        verb: ["declined the invitation"],
        subject: false,
        kind: "removed",
      };
    case "org.member_joined":
      return {
        verb: ["accepted the invitation and joined"],
        subject: false,
        kind: "joined",
      };
    case "org.member_left":
      return { verb: ["left the organization"], subject: false, kind: "left" };
    case "org.member_removed":
      return { verb: ["removed"], subject: true, kind: "removed" };
    case "org.owner_granted":
      return self
        ? { verb: ["made themselves an owner"], subject: false, kind: "role" }
        : { verb: ["made"], subject: true, tail: "an owner", kind: "role" };
    case "org.owner_revoked":
      return self
        ? {
            verb: ["gave up their own ownership"],
            subject: false,
            kind: "role",
          }
        : { verb: ["removed ownership from"], subject: true, kind: "role" };
    case "org.admin_granted":
      return self
        ? { verb: ["made themselves an admin"], subject: false, kind: "role" }
        : { verb: ["made"], subject: true, tail: "an admin", kind: "role" };
    case "org.admin_revoked":
      return self
        ? {
            verb: ["gave up their own admin role"],
            subject: false,
            kind: "role",
          }
        : { verb: ["removed admin from"], subject: true, kind: "role" };
    /// An action this build does not know: show it raw rather than drop the
    /// row — an unreadable governance act still has to be visible.
    case "billing.subscribed": {
      const seats = count(detail, "seats");
      return {
        verb: seats
          ? words`started a subscription of ${plural(seats, "seat")}`
          : words`started a subscription`,
        subject: false,
        kind: "billing",
      };
    }
    case "billing.attached": {
      const seats = count(detail, "seats");
      return {
        verb: seats
          ? words`attached a subscription of ${plural(seats, "seat")}`
          : words`attached an existing subscription`,
        subject: false,
        kind: "billing",
      };
    }
    case "billing.cancelled": {
      const at = text(detail, "effective_at");
      return {
        verb: at
          ? words`set the subscription to end on ${new Date(at).toLocaleDateString()}`
          : words`set the subscription to end`,
        subject: false,
        kind: "deleted",
      };
    }
    case "billing.resumed":
      return {
        verb: words`kept the subscription`,
        subject: false,
        kind: "restored",
      };
    case "billing.seats_changed": {
      const from = count(detail, "from");
      const to = count(detail, "to");
      return {
        verb:
          from !== null && to !== null
            ? words`changed the seats from ${String(from)} to ${String(to)}`
            : words`changed the seats`,
        subject: false,
        kind: "role",
      };
    }
    case "billing.details_changed": {
      const recipients = count(detail, "recipients");
      const tail = recipients
        ? `${plural(recipients, "invoice recipient")} on file`
        : undefined;
      return {
        verb: words`changed the invoice details`,
        subject: false,
        tail,
        kind: "billing",
      };
    }
    case "billing.contact_changed": {
      const from = text(detail, "from");
      return {
        verb: from
          ? words`moved billing from ${from} to`
          : words`set the billing address to`,
        subject: true,
        kind: "billing",
      };
    }
    /// Recorded on Paddle's word; `unattributed` is the payer replacing their
    /// own card with no form pending to name anybody.
    case "billing.card_changed":
      return {
        verb:
          entry.actor === "unattributed"
            ? words`A card was put on the subscription`
            : words`put a new card on the subscription`,
        subject: false,
        kind: "billing",
      };
    /// A MyCorr platform admin's act; the org's own log names the actor only
    /// as MyCorr Admin.
    case "platform.storage_limit_set": {
      const before = count(detail, "before");
      const after = count(detail, "after");
      return {
        verb: words`set the storage limit`,
        subject: false,
        tail:
          after === null
            ? undefined
            : `${before === null ? "unlimited" : formatBytes(before)} → ${formatBytes(after)}`,
        kind: "limit",
      };
    }
    case "platform.egress_reset": {
      const before = count(detail, "before");
      return {
        verb: words`reset this month's egress`,
        subject: false,
        tail: before === null ? undefined : `${formatBytes(before)} → 0`,
        kind: "reset",
      };
    }
    case "platform.mcp_calls_reset": {
      const before = count(detail, "before");
      return {
        verb: words`reset this month's tool calls`,
        subject: false,
        tail: before === null ? undefined : `${before.toLocaleString()} → 0`,
        kind: "reset",
      };
    }
    default:
      return { verb: [entry.action], subject: entry.subject !== null };
  }
}
